import React, { useState } from 'react';
import { Phone, MessageCircle, User, MapPin, Clock, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { api, companyInfo } from '../data/api';

const BookingSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    pickup: '',
    destination: '',
    date: '',
    time: '',
    notes: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleWhatsAppSubmit = async () => {
    setLoading(true);
    
    try {
      // Save booking to database
      await api.createBooking({
        name: formData.name,
        phone: formData.phone,
        pickup: formData.pickup,
        destination: formData.destination,
        date: formData.date || null,
        time: formData.time || null,
        notes: formData.notes || null
      });
    } catch (error) {
      console.log('Booking save error (continuing to WhatsApp):', error);
    }

    // Always redirect to WhatsApp
    const message = `Merhaba, taksi rezervasyonu yapmak istiyorum.

İsim: ${formData.name}
Telefon: ${formData.phone}
Alış Noktası: ${formData.pickup}
Varış Noktası: ${formData.destination}
Tarih: ${formData.date || 'Belirtilmedi'}
Saat: ${formData.time || 'Belirtilmedi'}
Not: ${formData.notes || 'Yok'}`;

    window.open(`https://wa.me/${companyInfo.phoneClean.replace('+', '')}?text=${encodeURIComponent(message)}`, '_blank');
    
    setSubmitted(true);
    setLoading(false);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        phone: '',
        pickup: '',
        destination: '',
        date: '',
        time: '',
        notes: ''
      });
    }, 3000);
  };

  const handleCall = () => {
    window.location.href = `tel:${companyInfo.phoneClean}`;
  };

  return (
    <section id="booking" className="py-20 md:py-28 bg-zinc-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-amber-600/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Info */}
          <div>
            <span className="inline-block text-amber-400 text-sm font-semibold tracking-wider uppercase mb-4">
              Taksi Çağır
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Hemen <span className="text-amber-400">Taksi Çağırın</span>
            </h2>
            <p className="text-zinc-400 text-lg mb-8">
              Formu doldurarak WhatsApp üzerinden anında taksi çağırabilir veya 
              doğrudan telefon ile arayabilirsiniz.
            </p>

            {/* Quick Contact Options */}
            <div className="space-y-4 mb-8">
              <Button
                onClick={handleCall}
                size="lg"
                className="w-full sm:w-auto bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold text-lg px-8 py-6 shadow-lg shadow-amber-500/30 transition-all duration-300 hover:scale-105"
              >
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phone}
              </Button>
            </div>

            {/* Features */}
            <div className="space-y-4">
              {[
                { icon: Clock, text: '7/24 Hizmet - Her zaman ulaşılabilir' },
                { icon: MapPin, text: 'Denizli genelinde hızlı ulaşım' },
                { icon: CheckCircle, text: 'Temiz ve bakımlı araçlar' }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 text-zinc-300">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-amber-400" />
                  </div>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-zinc-800/50 backdrop-blur-sm rounded-3xl p-6 md:p-8 border border-zinc-700/50">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Send className="w-5 h-5 text-amber-400" />
              WhatsApp ile Rezervasyon
            </h3>

            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">WhatsApp'a Yönlendirildiniz!</h4>
                <p className="text-zinc-400">Mesajınızı gönderin, size en kısa sürede dönüş yapalım.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-zinc-300 mb-2 block">
                      <User className="w-4 h-4 inline mr-1" /> Adınız
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Ad Soyad"
                      className="bg-zinc-900/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-zinc-300 mb-2 block">
                      <Phone className="w-4 h-4 inline mr-1" /> Telefon
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="0530 000 00 00"
                      className="bg-zinc-900/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-amber-500"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="pickup" className="text-zinc-300 mb-2 block">
                    <MapPin className="w-4 h-4 inline mr-1" /> Alış Noktası
                  </Label>
                  <Input
                    id="pickup"
                    name="pickup"
                    value={formData.pickup}
                    onChange={handleChange}
                    placeholder="Nereden alınacaksınız?"
                    className="bg-zinc-900/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-amber-500"
                  />
                </div>

                <div>
                  <Label htmlFor="destination" className="text-zinc-300 mb-2 block">
                    <MapPin className="w-4 h-4 inline mr-1" /> Varış Noktası
                  </Label>
                  <Input
                    id="destination"
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    placeholder="Nereye gideceksiniz?"
                    className="bg-zinc-900/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-amber-500"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date" className="text-zinc-300 mb-2 block">
                      <Clock className="w-4 h-4 inline mr-1" /> Tarih
                    </Label>
                    <Input
                      id="date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleChange}
                      className="bg-zinc-900/50 border-zinc-700 text-white focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="time" className="text-zinc-300 mb-2 block">
                      <Clock className="w-4 h-4 inline mr-1" /> Saat
                    </Label>
                    <Input
                      id="time"
                      name="time"
                      type="time"
                      value={formData.time}
                      onChange={handleChange}
                      className="bg-zinc-900/50 border-zinc-700 text-white focus:border-amber-500"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes" className="text-zinc-300 mb-2 block">Not (Opsiyonel)</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Ek bilgi veya özel istekleriniz..."
                    className="bg-zinc-900/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-amber-500 min-h-[80px]"
                  />
                </div>

                <Button
                  onClick={handleWhatsAppSubmit}
                  disabled={loading || !formData.name || !formData.phone || !formData.pickup || !formData.destination}
                  className="w-full bg-green-600 hover:bg-green-500 text-white font-semibold py-6 text-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Gönderiliyor...</>
                  ) : (
                    <><MessageCircle className="w-5 h-5 mr-2" /> WhatsApp ile Gönder</>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
